import styled from '@emotion/styled'
import { tokens } from '@news-mono/design-tokens'
import { TextLink } from '../../../../../../typography/TextLink/TextLink'
import { colors } from '../../../../../../__styling/settings/colors'
import { zIndex } from '../../../../../../__styling/settings/z-index'
import { breakpoint } from '../../../../../../__styling/style-functions/breakpoint'
import { calcRem } from '../../../../../../__styling/style-functions/calc-rem'
import {
    fluidTypography,
    transition,
} from '../../../../../../__styling/style-mixins'
import { themedValue } from '../../../../../../__styling/themed-value'
import { Theme } from '../../../../../../__styling/themes'

export const StyledBreachWallContent = styled('div')(
    ({
        theme,
        backgroundColor,
    }: {
        theme: Theme
        backgroundColor?: string
    }) => ({
        display: 'flex',
        flexDirection: 'column',
        flexWrap: 'wrap',
        alignItems: 'center',
        width: '100%',
        position: 'relative',
        zIndex: zIndex.common.regWall.box,
        fontFamily: theme.fonts.subscriber.text,
        fontSize: calcRem(18),
        color: colors.white,
        padding: calcRem(theme.margins.md, 0),
        backgroundBlendMode: 'overlay, normal',

        backgroundColor:
            backgroundColor ||
            themedValue(theme, {
                thewest: theme.sectionValues.primaryColor,
                perthnow: colors.perthnow.greyBasalt90,
                sevennews: colors.sevennews.charade,
            }),
        backgroundRepeat: 'no-repeat',
        backgroundSize: '100% auto',
        backgroundPosition: 'top',
        [breakpoint('xxs')]: {
            paddingTop: calcRem(theme.margins.lg),
            paddingBottom: calcRem(theme.margins.md),
        },

        [breakpoint('md')]: {
            backgroundPosition: 'right',
            backgroundSize: 'cover',
        },
    }),
)

interface StyledBreachWallContent2022Props {
    largeImage: string
    smallImage: string
}
export const StyledBreachWallContent2022 = styled(StyledBreachWallContent)(
    ({ largeImage, smallImage }: StyledBreachWallContent2022Props) => ({
        padding: calcRem(120, 24, 24, 24),
        flexDirection: 'row',
        backgroundBlendMode: 'revert',
        backgroundImage: smallImage ? `url('${smallImage}')` : undefined,

        [breakpoint('xxs')]: {
            padding: calcRem(30, 24, 24, 18),
        },

        [breakpoint('sm')]: {
            backgroundImage: largeImage ? `url('${largeImage}')` : undefined,
        },

        '&.breach-190-year': {
            backgroundImage: 'unset',
            background:
                tokens.thewest.colors.palette.anniversary190YearGradient,
            '@media screen and (max-width:500px)': {
                paddingTop: calcRem(20),
            },
        },
    }),
)

export const StyledRegistrationInfo = styled('div')(({ theme }) => ({}))

export const StyledTerms2022 = styled.span<{ breachStyle: string }>(
    ({ theme, breachStyle }) => ({
        flex: '0 1 100%',
        width: 0,
        border: 'none',
        borderRadius: calcRem(5),
        fontSize: calcRem(16),
        textAlign: 'center',
        textTransform: 'initial',
        padding: '1rem',
        justifySelf: 'center',
        maxWidth:
            breachStyle === 'breach-190-year' ? calcRem(652) : calcRem(636),

        backgroundColor: theme.colors.background.primary,
        color: theme.colors.text.primary,
        fontFamily: theme.fonts.serif,
    }),
)
// )

const fluidTitle = (theme: Theme) =>
    themedValue(theme, {
        sevennews: fluidTypography(0, 1000, 24, 30),
        perthnow: fluidTypography(0, 1020, 24, 30),
        thewest: fluidTypography(0, 1220, 48, 56),
    })

export const StyledTitle = styled('h2')(({ theme }) => ({
    position: 'relative',
    margin: calcRem(theme.margins.sm),
    fontWeight: 700,
    textAlign: 'center',
    lineHeight: 1.267,

    ...fluidTitle(theme),

    [breakpoint('xs')]: {
        margin: calcRem(0, 0, theme.margins.sm, 0),
    },
}))

export const StyledMessage = styled('div')(({ theme }) => ({
    margin: 'auto',
    marginBottom: calcRem(theme.margins.sm),
    maxWidth: calcRem(500),
    p: {
        margin: calcRem(0, theme.margins.sm),
        fontsize: calcRem(20),
        textAlign: 'center',
    },
}))

export const StyledPackage = styled('div')(({ theme }) => ({
    flexGrow: 1,
    flexBasis: 400,
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'center',
    margin: 0,

    [breakpoint('xs')]: {
        margin: calcRem(0, theme.margins.md),
    },
}))

export const StyledPackage2022 = styled.div<{ breachStyle: string }>(
    ({ theme, breachStyle }) => ({
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        alignSelf: 'stretch',
        justifyContent: breachStyle == 'breach-190-year' ? 'center' : undefined,
    }),
)

export const StyledButton = styled('button')(({ theme }) => ({
    display: 'inline-block',
    border: `1px solid ${colors.white}`,
    padding: calcRem(theme.margins.sm, theme.margins.xl),
    marginTop: calcRem(theme.margins.lg),
    background: 'none',
    color: colors.white,
    borderRadius: calcRem(2),
    fontFamily: theme.fonts.sansSerif,
    fontWeight: 700,
    fontSize: calcRem(13),
    textDecoration: 'none',
    textTransform: 'uppercase',
    cursor: 'pointer',
    ...transition({ properties: ['background', 'color'] }),

    '&:hover': {
        backgroundColor: colors.white,
        color: themedValue(theme, {
            thewest: colors.thewest.greyGorilla,
            perthnow: colors.perthnow.blueKyanite,
            sevennews: colors.sevennews.red,
        }),
    },

    '&:focus': {
        backgroundColor: themedValue(theme, {
            thewest: colors.thewest.greyGorilla,
            perthnow: colors.perthnow.pinkThulite,
            sevennews: colors.sevennews.red,
        }),
        borderColor: themedValue(theme, {
            thewest: colors.thewest.greyGorilla,
            perthnow: colors.perthnow.pinkThulite,
            sevennews: colors.sevennews.red,
        }),
        color: 'currentColor',
        outline: 'none',
        textDecoration: 'underline',
    },
}))

export const StyledPrintDigTitle = styled(StyledTitle)({
    fontSize: calcRem(23), // DPT-3294: campaign requires specific sizing
})

export const StyledTerms = styled('p')(({ theme }) => ({
    color: colors.white,
    margin: 0,
    marginTop: calcRem(theme.margins.sm),
    fontSize: calcRem(11),
}))

export const StyledLiveTrailerWrap = styled('div')({
    margin: `${calcRem(30)} auto`,
    boxShadow: '0 0 16px rgba(0,0,0,0.5)',
})

export const StyledHelpMessage = styled('p')(({ theme }) => ({
    margin: calcRem(theme.margins.sm, 0),
}))

export const StyledTextLink = styled(TextLink)<{ reverseColor: boolean }>(
    ({ reverseColor }) => ({
        color: reverseColor ? colors.white : undefined,
    }),
)
